import { inject, Injectable } from '@angular/core';
import { Overlay, PositionStrategy } from '@angular/cdk/overlay';
import { ToastsComponent } from '../components/toast/toast.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { ToastOutlet, ToastType } from '../types/enums';
import { Toast, ToastOutletState } from '../types/toast.types';
import { TOAST_OUTLET_POSITION_CONFIG } from '../types/constants';

@Injectable({ providedIn: 'root' })
export class ToastsService {
  private readonly overlay = inject(Overlay);

  private readonly outletStates = new Map<ToastOutlet, ToastOutletState>();
  private readonly outletHosts = new Map<ToastOutlet, HTMLElement>();
  private readonly outletResizeObservers = new Map<ToastOutlet, ResizeObserver>();

  public success(message: string, outlet: ToastOutlet = ToastOutlet.Global): void {
    const toasts: Toast[] = [{ message, type: ToastType.Success }];
    this.batchShow(toasts, outlet);
  }

  public error(message: string, outlet: ToastOutlet = ToastOutlet.Global): void {
    const toasts: Toast[] = [{ message, type: ToastType.Error }];
    this.batchShow(toasts, outlet);
  }

  public batchShow(toasts: Toast[], outlet: ToastOutlet = ToastOutlet.Global): void {
    const state = this.getOutletState(outlet);

    if (!state.overlayRef || !state.componentRef) {
      this.initOverlayRef(toasts, outlet);
    } else {
      state.componentRef.instance.toasts = [...state.componentRef.instance.toasts, ...toasts];
    }
  }

  public clear(outlet: ToastOutlet = ToastOutlet.Global): void {
    const state = this.outletStates.get(outlet);

    if (!state) {
      return;
    }

    if (state.componentRef) {
      state.componentRef.instance.toasts = [];
    }

    state.overlayRef?.dispose();
    state.overlayRef = undefined;
    state.componentRef = undefined;
  }

  public registerHost(outlet: ToastOutlet, hostElement: HTMLElement): void {
    if (outlet === ToastOutlet.Global) {
      return;
    }

    this.outletHosts.set(outlet, hostElement);
    this.disconnectResizeObserver(outlet);

    const resizeObserver = new ResizeObserver(() => {
      this.updateOutletPosition(outlet);
    });
    resizeObserver.observe(hostElement);
    this.outletResizeObservers.set(outlet, resizeObserver);

    this.updateOutletPosition(outlet);
  }

  public unregisterHost(outlet: ToastOutlet): void {
    this.outletHosts.delete(outlet);
    this.disconnectResizeObserver(outlet);
    this.updateOutletPosition(outlet);
  }

  private initOverlayRef(toasts: Toast[], outlet: ToastOutlet): void {
    const state = this.getOutletState(outlet);

    state.overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.buildPositionStrategy(outlet),
      minHeight: '50px',
      maxWidth: '400px',
      panelClass: 'toasts-overlay',
    });

    const toastsComponentPortal = new ComponentPortal(ToastsComponent);

    state.componentRef = state.overlayRef.attach(toastsComponentPortal);
    state.componentRef.instance.overlayRef = state.overlayRef;
    state.componentRef.instance.toastOutlet = outlet;
    state.componentRef.instance.toasts = toasts;
  }

  public disposeIfNoToasts(outlet: ToastOutlet): void {
    const state = this.getOutletState(outlet);

    if (state.componentRef && state.componentRef.instance.toasts.length === 0) {
      state.overlayRef?.dispose();
      state.overlayRef = undefined;
      state.componentRef = undefined;
    }
  }

  private getOutletState(outlet: ToastOutlet): ToastOutletState {
    if (!this.outletStates.has(outlet)) {
      this.outletStates.set(outlet, {});
    }

    return this.outletStates.get(outlet)!;
  }

  private buildPositionStrategy(outlet: ToastOutlet): PositionStrategy {
    const positionConfig = TOAST_OUTLET_POSITION_CONFIG[outlet];

    if (outlet === ToastOutlet.MainMap) {
      const hostElement = this.outletHosts.get(ToastOutlet.MainMap);

      if (hostElement) {
        return this.overlay
          .position()
          .flexibleConnectedTo(hostElement)
          .withPush(false)
          .withPositions([
            {
              originX: 'end',
              originY: 'bottom',
              overlayX: 'end',
              overlayY: 'bottom',
              offsetX: -positionConfig.rightPx,
              offsetY: -positionConfig.bottomPx,
            },
          ]);
      }
    }

    return this.overlay.position().global().right(`${positionConfig.rightPx}px`).bottom(`${positionConfig.bottomPx}px`);
  }

  private updateOutletPosition(outlet: ToastOutlet): void {
    const state = this.outletStates.get(outlet);

    if (!state?.overlayRef) {
      return;
    }

    const positionStrategy = this.buildPositionStrategy(outlet);
    state.overlayRef.updatePositionStrategy(positionStrategy);
    state.overlayRef.updatePosition();
  }

  private disconnectResizeObserver(outlet: ToastOutlet): void {
    const resizeObserver = this.outletResizeObservers.get(outlet);

    if (!resizeObserver) {
      return;
    }

    resizeObserver.disconnect();
    this.outletResizeObservers.delete(outlet);
  }
}
